import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, LayoutGrid, RefreshCw, Wifi, WifiOff } from 'lucide-react';
import { AnimatePresence, motion } from 'framer-motion';
import Button from '../components/ui/Button';
import PropertyCard from '../components/PropertyCard';
import { fetchProperties } from '../lib/nettyService';
import { MOCK_PROPERTIES } from '../data/mocks';
import { groupProperties } from '../lib/grouping';

const SITE_TARGET = import.meta.env.VITE_SITE_TARGET || 'scbi';

const Properties = () => {
    const navigate = useNavigate();
    const [properties, setProperties] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isLive, setIsLive] = useState(false); // true = données Netty live, false = mocks
    const [sortOrder, setSortOrder] = useState('default');

    useEffect(() => {
        const loadProperties = async () => {
            setLoading(true);
            try {
                const data = await fetchProperties(SITE_TARGET);
                if (data && data.length > 0) {
                    setProperties(groupProperties(data));
                    setIsLive(true);
                } else {
                    // Fallback gracieux sur les mocks si Supabase est vide ou non configuré
                    setProperties(groupProperties(MOCK_PROPERTIES));
                    setIsLive(false);
                }
            } catch (e) {
                console.warn('[Borbiconi] Supabase unavailable, using mock data:', e);
                setProperties(groupProperties(MOCK_PROPERTIES));
                setIsLive(false);
            } finally {
                setLoading(false);
            }
        };

        loadProperties();
        document.title = 'Toutes les annonces immobilières - Borbiconi Immobilier';
    }, []);

    const containerVariants = {
        hidden: { opacity: 0 },
        show: {
            opacity: 1,
            transition: { staggerChildren: 0.08 }
        }
    };

    const getSortedProperties = () => {
        let sorted = [...properties];

        // Sort by price if requested
        if (sortOrder === 'price-asc') {
            sorted.sort((a, b) => (a.price || 0) - (b.price || 0));
        } else if (sortOrder === 'price-desc') {
            sorted.sort((a, b) => (b.price || 0) - (a.price || 0));
        }

        // Always sort by availability priority to keep "Vendu" etc. at the bottom
        sorted.sort((a, b) => {
            const priority = (p) => {
                const ms = (p.marketing_status || 'Disponible').toLowerCase();
                if (ms === 'vendu' || ms === 'loué' || ms.includes('compromis')) return -2000;
                if (ms.includes('offre')) return 0;
                return 0;
            };
            const pA = priority(a);
            const pB = priority(b);
            
            if (pA === pB) return 0;
            return pB - pA;
        });

        return sorted;
    };

    const displayedProperties = getSortedProperties();

    return (
        <div className="bg-[#F7F7F7] min-h-screen pt-24 pb-20 font-sans">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                {/* Header Section */}
                <div className="flex flex-col md:flex-row justify-between items-center mb-12 gap-6">
                    <div className="text-center md:text-left">
                        <button
                            onClick={() => navigate('/')}
                            className="flex items-center gap-2 text-[#EA723D] font-bold text-sm mb-4 hover:gap-3 transition-all"
                        >
                            <ArrowLeft size={18} /> Retour à l'accueil
                        </button>
                        <h1 className="text-4xl md:text-5xl font-display font-black text-[#2C353D]">Toutes nos Annonces</h1>
                        <div className="w-24 h-1.5 bg-[#EA723D] mt-3 rounded-full mx-auto md:mx-0"></div>
                    </div>

                    <div className="bg-white p-4 rounded-2xl shadow-sm border border-gray-100 flex items-center gap-4">
                        <div className="bg-[#2C353D]/5 p-3 rounded-xl text-[#2C353D] hidden sm:block">
                            <LayoutGrid size={24} />
                        </div>
                        <div>
                            <p className="text-sm font-bold text-[#2C353D]">{displayedProperties.length} Biens Disponibles</p>
                            <p className="text-xs text-gray-400 font-medium italic">Sélection mise à jour quotidiennement</p>
                        </div>
                        <div className="ml-2 pl-4 border-l border-gray-100">
                            <select
                                value={sortOrder}
                                onChange={(e) => setSortOrder(e.target.value)}
                                className="bg-gray-50 border border-gray-200 text-[#2C353D] text-sm rounded-xl focus:ring-[#EA723D] focus:border-[#EA723D] block w-full p-2 font-medium outline-none cursor-pointer"
                            >
                                <option value="default">Tri par défaut</option>
                                <option value="price-asc">Prix croissant</option>
                                <option value="price-desc">Prix décroissant</option>
                            </select>
                        </div>
                    </div>
                </div>

                {loading ? (
                    <div className="text-center py-40 flex flex-col items-center">
                        <div className="w-16 h-16 border-4 border-[#EA723D] border-t-transparent rounded-full animate-spin mb-4"></div>
                        <p className="text-gray-400 font-bold uppercase tracking-widest text-xs">Chargement des biens...</p>
                    </div>
                ) : (
                    <>
                        <motion.div
                            variants={containerVariants}
                            initial="hidden"
                            animate="show"
                            className="grid md:grid-cols-2 lg:grid-cols-3 gap-8"
                        >
                            <AnimatePresence mode="wait">
                                {displayedProperties.map(property => (
                                    <PropertyCard
                                        key={property.id || property.netty_ref}
                                        property={property}
                                        onClick={() => navigate(`/property/${property.id}`)}
                                    />
                                ))}
                            </AnimatePresence>
                        </motion.div>

                        {displayedProperties.length === 0 && (
                            <div className="text-center py-20 bg-white border border-dashed border-gray-200 rounded-3xl">
                                <p className="text-gray-500 mb-6 font-medium">Aucun bien n'est disponible pour le moment.</p>
                                <Button onClick={() => navigate('/')} variant="outline">
                                    Retour à l'accueil
                                </Button>
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

export default Properties;
